import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://readdy.ai/api/form/d6ghtujsl82c26afkn1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', date: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openLightbox = (src: string) => setLightboxSrc(src);
  const closeLightbox = () => setLightboxSrc(null);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EBEBEB' }}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || mobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-4 md:py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/431d0d82-5d35-4e5b-9098-9c3881ac0720.png"
              alt="Hair Salon Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-12">
            <a href="#home" className={`text-sm font-medium tracking-wider uppercase transition-colors ${isScrolled ? 'text-gray-800 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Home</a>
            <Link to="/services" className={`text-sm font-medium tracking-wider uppercase transition-colors ${isScrolled ? 'text-gray-800 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Services</Link>
            <a href="#gallery" className={`text-sm font-medium tracking-wider uppercase transition-colors ${isScrolled ? 'text-gray-800 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Gallery</a>
            <a href="#about" className={`text-sm font-medium tracking-wider uppercase transition-colors ${isScrolled ? 'text-gray-800 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>About</a>
            <Link to="/media" className={`text-sm font-medium tracking-wider uppercase transition-colors ${isScrolled ? 'text-gray-800 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Press</Link>
            <a href="#contact" className={`text-sm font-medium tracking-wider uppercase transition-colors ${isScrolled ? 'text-gray-800 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Contact</a>
          </div>

          <a
            href="https://joshuastinnetthair.booksy.com/a/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:flex bg-[#C9A961] text-white px-8 py-3 rounded-full text-sm font-medium tracking-wide uppercase hover:bg-[#B89851] transition-all whitespace-nowrap cursor-pointer items-center gap-2"
          >
            Book Now
            <i className="ri-arrow-right-line"></i>
          </a>

          {/* Mobile: Book + Hamburger */}
          <div className="flex lg:hidden items-center gap-3">
            <a
              href="https://joshuastinnetthair.booksy.com/a/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#C9A961] text-white px-5 py-2 rounded-full text-xs font-medium tracking-wide uppercase whitespace-nowrap cursor-pointer"
            >
              Book Now
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled || mobileMenuOpen ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-5 py-6 flex flex-col gap-5">
            <a href="#home" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium tracking-wider uppercase text-gray-800 hover:text-[#C9A961] transition-colors">Home</a>
            <Link to="/services" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium tracking-wider uppercase text-gray-800 hover:text-[#C9A961] transition-colors">Services</Link>
            <a href="#gallery" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium tracking-wider uppercase text-gray-800 hover:text-[#C9A961] transition-colors">Gallery</a>
            <a href="#about" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium tracking-wider uppercase text-gray-800 hover:text-[#C9A961] transition-colors">About</a>
            <Link to="/media" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium tracking-wider uppercase text-gray-800 hover:text-[#C9A961] transition-colors">Press</Link>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium tracking-wider uppercase text-gray-800 hover:text-[#C9A961] transition-colors">Contact</a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-end md:items-center justify-center px-4 md:px-6 pt-20">
        <div className="absolute inset-3 md:inset-6 rounded-[20px] md:rounded-[30px] overflow-hidden">
          <img
            src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/a851a17eafa88b7a7e374c6a3befa6a0.jpeg"
            alt="Hair by Joshua Stinnett"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 15%' }}
          />
          {/* Mobile: only darken the bottom so face stays clear. Desktop: original gradient */}
          <div className="absolute inset-0 md:hidden bg-gradient-to-t from-black/75 via-black/10 to-black/0"></div>
          <div className="absolute inset-0 hidden md:block bg-gradient-to-b from-black/50 via-black/30 to-black/40"></div>
        </div>

        {/* Floating JS monogram — hide on mobile so it doesn't cover the face */}
        <img
          src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/6d37faf3f9bfa522e2e90cd6bfd38070.png"
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none hidden md:block"
          style={{
            opacity: 0.28,
            width: 'min(560px, 85vw)',
            height: 'min(560px, 85vw)',
            objectFit: 'contain',
            zIndex: 5,
            filter: 'brightness(2) blur(0.5px) drop-shadow(0 0 30px rgba(255,255,255,0.15))',
            marginTop: '-8vh',
            mixBlendMode: 'screen'
          }}
        />

        {/* Mobile: text pinned to bottom. Desktop: centered with negative margin */}
        <div
          className="relative text-center max-w-4xl mx-auto w-full px-4 pb-44 md:pb-0"
          style={{ zIndex: 10, marginTop: 'clamp(0px, 0px, 0px)' }}
        >
          {/* marginTop only on desktop */}
          <style>{`@media (min-width: 768px) { .hero-inner { margin-top: -10vh; } }`}</style>
          <div className="hero-inner">
            <div className="inline-block mb-3 md:mb-6 px-3 md:px-6 py-1.5 md:py-2 border border-white/40 md:border-2 rounded-full backdrop-blur-sm">
              <span className="text-white text-[10px] md:text-xs font-medium tracking-widest uppercase">West Hollywood · Modern Hair Artistry</span>
            </div>

            <h1 className="text-white font-serif mb-2 md:mb-4 leading-tight tracking-wide" style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.6rem, 6vw, 3.2rem)' }}>
              Joshua Stinnett
            </h1>

            <p className="text-white/80 text-xs md:text-lg mb-1.5 md:mb-3 tracking-widest uppercase font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
              Refined Technique · Effortless Results
            </p>

            <p className="text-white/70 text-[11px] md:text-base mb-6 md:mb-10 tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
              Precision cutting, advanced color, and luxury service — tailored to you
            </p>

            <a
              href="#services"
              className="inline-block bg-[#C9A961] text-white px-6 md:px-10 py-2.5 md:py-4 rounded-full text-xs md:text-sm font-medium tracking-wide uppercase hover:bg-[#B89851] transition-all whitespace-nowrap cursor-pointer"
            >
              Explore Services
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 md:py-32 px-4 md:px-6 relative" style={{ backgroundColor: '#DCDCDC' }}>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="text-center mb-8 md:mb-20">
            <div className="inline-block mb-3 md:mb-4 px-4 md:px-6 py-1.5 md:py-2 border border-gray-300 rounded-full">
              <span className="text-gray-600 text-[10px] md:text-xs font-medium tracking-widest uppercase">Specialties</span>
            </div>
            <h2 className="text-2xl md:text-5xl lg:text-6xl font-serif mb-3 md:mb-6 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Signature Services
            </h2>
            <p className="text-gray-600 text-sm md:text-lg max-w-2xl mx-auto">
              Nearly two decades of expertise behind the chair — every service is personal, precise, and built to last
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            <div className="rounded-2xl md:rounded-3xl p-6 md:p-10 transition-all duration-300 hover:bg-[#2C2C2C] hover:shadow-2xl group cursor-pointer" style={{ backgroundColor: '#EBEBEB' }}>
              <div className="w-11 h-11 md:w-16 md:h-16 rounded-full flex items-center justify-center mb-4 md:mb-6 group-hover:bg-[#C9A961] transition-colors" style={{ backgroundColor: '#D8D8D8' }}>
                <i className="ri-scissors-line text-lg md:text-2xl text-gray-700 group-hover:text-white"></i>
              </div>
              <h3 className="text-base md:text-2xl font-bold mb-2 md:mb-4 text-gray-900 group-hover:text-white transition-colors">Precision Cutting</h3>
              <p className="text-gray-600 mb-4 md:mb-6 leading-relaxed group-hover:text-gray-300 transition-colors text-xs md:text-base">
                From modern men's cuts to lived-in lengths, Joshua's cuts are rooted in balance, structure, and wearability. Hair that looks effortless in real life and grows out beautifully.
              </p>
              <a href="https://joshuastinnetthair.booksy.com/a/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#C9A961] font-medium group-hover:text-white transition-colors cursor-pointer text-xs md:text-sm">
                Book Now <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="rounded-2xl md:rounded-3xl p-6 md:p-10 transition-all duration-300 hover:bg-[#2C2C2C] hover:shadow-2xl group cursor-pointer" style={{ backgroundColor: '#EBEBEB' }}>
              <div className="w-11 h-11 md:w-16 md:h-16 rounded-full flex items-center justify-center mb-4 md:mb-6 group-hover:bg-[#C9A961] transition-colors" style={{ backgroundColor: '#D8D8D8' }}>
                <i className="ri-palette-line text-lg md:text-2xl text-gray-700 group-hover:text-white"></i>
              </div>
              <h3 className="text-base md:text-2xl font-bold mb-2 md:mb-4 text-gray-900 group-hover:text-white transition-colors">Advanced Color &amp; Blonding</h3>
              <p className="text-gray-600 mb-4 md:mb-6 leading-relaxed group-hover:text-gray-300 transition-colors text-xs md:text-base">
                Signature platinum blondes, seamless balayage, and bold vivid color — achieved with advanced techniques and a commitment to hair health and longevity.
              </p>
              <a href="https://joshuastinnetthair.booksy.com/a/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#C9A961] font-medium group-hover:text-white transition-colors cursor-pointer text-xs md:text-sm">
                Book Now <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="rounded-2xl md:rounded-3xl p-6 md:p-10 transition-all duration-300 hover:bg-[#2C2C2C] hover:shadow-2xl group cursor-pointer" style={{ backgroundColor: '#EBEBEB' }}>
              <div className="w-11 h-11 md:w-16 md:h-16 rounded-full flex items-center justify-center mb-4 md:mb-6 group-hover:bg-[#C9A961] transition-colors" style={{ backgroundColor: '#D8D8D8' }}>
                <i className="ri-magic-line text-lg md:text-2xl text-gray-700 group-hover:text-white"></i>
              </div>
              <h3 className="text-base md:text-2xl font-bold mb-2 md:mb-4 text-gray-900 group-hover:text-white transition-colors">Extensions &amp; Styling</h3>
              <p className="text-gray-600 mb-4 md:mb-6 leading-relaxed group-hover:text-gray-300 transition-colors text-xs md:text-base">
                Seamless weft extensions, red carpet styling, and press-ready looks for high-profile events, award shows, and private appearances — delivered with discretion and polish.
              </p>
              <a href="https://joshuastinnetthair.booksy.com/a/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#C9A961] font-medium group-hover:text-white transition-colors cursor-pointer text-xs md:text-sm">
                Book Now <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-8 md:py-32 px-4 md:px-6 relative" style={{ backgroundColor: '#EBEBEB' }}>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="text-center mb-4 md:mb-20">
            <h2 className="text-xl md:text-5xl lg:text-6xl font-serif mb-1.5 md:mb-6 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Portfolio
            </h2>
            <p className="text-gray-600 text-[11px] md:text-lg">Created for camera, stage, and everyday life.</p>
          </div>

          {/* Row 1 */}
          <div className="grid grid-cols-3 md:grid-cols-3 gap-1.5 md:gap-5 mb-1.5 md:mb-5">
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(90px, 28vw, 180px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/9b6d40ede09cc141f9cf96a6606ddb05.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/9b6d40ede09cc141f9cf96a6606ddb05.jpeg" alt="Hair Transformation" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(90px, 28vw, 180px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/beab011d22fa7a891c8a702296146923.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/beab011d22fa7a891c8a702296146923.jpeg" alt="Vibrant Orange Hair" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ objectPosition: 'center 35%' }} />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(90px, 28vw, 180px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/dfccacd91e97d61cb57b5b77ef3b2fad.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/dfccacd91e97d61cb57b5b77ef3b2fad.jpeg" alt="Bridal Hair Styling" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-4 md:grid-cols-4 gap-1.5 md:gap-5 mb-1.5 md:mb-5">
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(70px, 22vw, 130px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/6e337736565a634835a778b4d2dbc3aa.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/6e337736565a634835a778b4d2dbc3aa.jpeg" alt="Precision Cut" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" style={{ backgroundColor: '#D8D8D8' }} />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(70px, 22vw, 130px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/9fbedd3ffa2da4680ffff333f3a41ab5.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/9fbedd3ffa2da4680ffff333f3a41ab5.jpeg" alt="Vogue Feature" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(70px, 22vw, 130px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/d6fc8c928c84efa1848e36edb8c55f8d.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/d6fc8c928c84efa1848e36edb8c55f8d.jpeg" alt="Bridal Hair Styling" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(70px, 22vw, 130px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/4c1d1909f8523a986ef0cdcee5d3624c.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/4c1d1909f8523a986ef0cdcee5d3624c.jpeg" alt="Red Carpet Event" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-5 md:grid-cols-5 gap-1.5 md:gap-5">
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(55px, 17vw, 110px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/1052f61c753473596e9014f896009d86.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/1052f61c753473596e9014f896009d86.jpeg" alt="Blonde Balayage" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(55px, 17vw, 110px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/c756d52676f28cd4529685590539c22f.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/c756d52676f28cd4529685590539c22f.jpeg" alt="Red Carpet Styling" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(55px, 17vw, 110px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/7b08d38a5a86f61e0a8fc2856ec01894.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/7b08d38a5a86f61e0a8fc2856ec01894.jpeg" alt="Editorial Hair" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(55px, 17vw, 110px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/4762204994ffe8bcd56cd99e2f614ec2.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/4762204994ffe8bcd56cd99e2f614ec2.jpeg" alt="Fashion Portrait" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="overflow-hidden rounded-lg md:rounded-xl group cursor-pointer" style={{ height: 'clamp(55px, 17vw, 110px)' }} onClick={() => openLightbox("https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/e2e5f5172d80e6c68f288762e569612b.jpeg")}>
              <img src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/e2e5f5172d80e6c68f288762e569612b.jpeg" alt="Magazine Cover Hair" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-8 md:py-32 px-4 md:px-6 relative" style={{ backgroundColor: '#DCDCDC' }}>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-5 md:gap-16 items-center">
            <div className="md:col-span-3 order-2 md:order-1">
              <div className="mb-1.5 md:mb-6">
                <span className="text-gray-500 text-[9px] md:text-xs font-medium tracking-widest uppercase">About Joshua</span>
              </div>
              <h2 className="text-lg md:text-5xl font-serif mb-3 md:mb-10 text-gray-900 leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
                Nearly Two Decades<br />Behind the Chair
              </h2>

              <div className="text-gray-700 mb-4 md:mb-16" style={{ fontFamily: 'Inter, sans-serif' }}>
                <p className="text-[11px] md:text-lg leading-[1.6] md:leading-[1.8] mb-2 md:mb-6">
                  Joshua Stinnett is a Los Angeles based celebrity hairstylist and color specialist with nearly two decades of experience.
                </p>
                <p className="text-[11px] md:text-lg leading-[1.6] md:leading-[1.8] mb-2 md:mb-6">
                  Originally trained in Dallas at a high-end salon in Highland Park Village, he built his foundation on precision cutting, advanced blonding, and luxury client service.
                </p>
                <p className="text-[11px] md:text-lg leading-[1.6] md:leading-[1.8] mb-2 md:mb-6">
                  In 2014, Joshua relocated to West Hollywood, expanding into entertainment, music, and media — styling for red carpets, press events, and award shows.
                </p>
                <p className="text-[11px] md:text-lg leading-[1.6] md:leading-[1.8]">
                  Today, he operates from his private West Hollywood studio, offering a personalized, one-on-one experience tailored to each client's lifestyle.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-x-5 md:gap-x-16 gap-y-3 md:gap-y-12">
                <div>
                  <div className="text-xl md:text-6xl font-bold text-gray-900 mb-0.5 md:mb-2 leading-none">18+</div>
                  <div className="text-gray-600 text-[9px] md:text-sm tracking-wide mt-0.5">Years of Experience</div>
                </div>
                <div>
                  <div className="text-xl md:text-6xl font-bold text-gray-900 mb-0.5 md:mb-2 leading-none">2014</div>
                  <div className="text-gray-600 text-[9px] md:text-sm tracking-wide mt-0.5">West Hollywood Since</div>
                </div>
                <div>
                  <div className="text-xl md:text-6xl font-bold text-gray-900 mb-0.5 md:mb-2 leading-none">100%</div>
                  <div className="text-gray-600 text-[9px] md:text-sm tracking-wide mt-0.5">Private Studio Experience</div>
                </div>
                <div>
                  <div className="text-sm md:text-4xl font-bold text-gray-900 mb-0.5 md:mb-2 leading-tight">Red Carpet</div>
                  <div className="text-gray-600 text-[9px] md:text-sm tracking-wide mt-0.5">Events &amp; High-Profile Styling</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-2 order-1 md:order-2">
              <div
                className="rounded-2xl overflow-hidden w-full"
                style={{ height: 'clamp(300px, 75vw, 420px)' }}
              >
                <img
                  src="https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/37c8a0f40114c180a5d5e620a1d42aff.png"
                  alt="Joshua Stinnett Celebrity Hairstylist West Hollywood"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 10%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 md:py-32 px-4 md:px-6 bg-[#1A1A1A] relative">
        <div className="absolute inset-0 bg-[#1A1A1A]"></div>
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-5xl lg:text-6xl font-serif mb-3 md:mb-6 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Book a Consultation
            </h2>
            <div className="w-14 md:w-20 h-0.5 md:h-1 bg-[#C9A961] mx-auto mb-3 md:mb-6"></div>
            <p className="text-gray-400 text-sm md:text-lg">Private studio appointments available in West Hollywood</p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-3 md:p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-center text-sm">
              Thank you! We'll get back to you soon.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mb-6 p-3 md:p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-center text-sm">
              Something went wrong. Please try again.
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="mb-8 md:mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 mb-3 md:mb-6">
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Your Name" required className="w-full bg-[#2A2A2A] text-white px-4 md:px-6 py-3 md:py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A961] placeholder-gray-500 text-xs md:text-sm" />
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Your Email" required className="w-full bg-[#2A2A2A] text-white px-4 md:px-6 py-3 md:py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A961] placeholder-gray-500 text-xs md:text-sm" />
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="Phone Number" required className="w-full bg-[#2A2A2A] text-white px-4 md:px-6 py-3 md:py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A961] placeholder-gray-500 text-xs md:text-sm" />
              <select name="service" value={formData.service} onChange={handleInputChange} required className="w-full bg-[#2A2A2A] text-white px-4 md:px-6 py-3 md:py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A961] text-xs md:text-sm">
                <option value="">Select Service</option>
                <option value="Precision Cutting">Precision Cutting</option>
                <option value="Color Artistry">Color Artistry</option>
                <option value="Styling & Treatments">Styling &amp; Treatments</option>
                <option value="Consultation">Consultation</option>
              </select>
              <input type="date" name="date" value={formData.date} onChange={handleInputChange} required className="w-full bg-[#2A2A2A] text-white px-4 md:px-6 py-3 md:py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A961] placeholder-gray-500 text-xs md:text-sm" />
              <textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Your Message" maxLength={500} rows={1} className="w-full bg-[#2A2A2A] text-white px-4 md:px-6 py-3 md:py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A961] placeholder-gray-500 text-xs md:text-sm resize-none"></textarea>
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-[#C9A961] text-white py-3 md:py-5 rounded-full text-xs md:text-sm font-medium tracking-wide uppercase hover:bg-[#B89851] transition-all flex items-center justify-center gap-2 md:gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
              {isSubmitting ? 'Sending...' : 'Schedule Consultation'}
              <i className="ri-arrow-right-line"></i>
            </button>
          </form>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 text-center">
            <div>
              <div className="text-[#C9A961] text-[10px] md:text-xs font-medium tracking-widest uppercase mb-2 md:mb-3">Location</div>
              <div className="text-white text-sm md:text-base">Private Studio</div>
              <div className="text-gray-400 text-sm md:text-base">West Hollywood, CA</div>
            </div>
            <div>
              <div className="text-[#C9A961] text-[10px] md:text-xs font-medium tracking-widest uppercase mb-2 md:mb-3">Contact</div>
              <div className="text-white text-sm md:text-base mb-1">Joshua Stinnett</div>
              <a href="mailto:JoshuaStinnett85@gmail.com" className="text-gray-400 text-xs md:text-sm hover:text-[#C9A961] transition-colors cursor-pointer">JoshuaStinnett85@gmail.com</a>
              <a href="tel:3107708491" className="text-gray-400 text-xs md:text-sm hover:text-[#C9A961] transition-colors cursor-pointer">310.770.8491</a>
            </div>
            <div>
              <div className="text-[#C9A961] text-[10px] md:text-xs font-medium tracking-widest uppercase mb-2 md:mb-3">Instagram</div>
              <a href="https://www.instagram.com/joshuastinnett?igsh=NTc4MTIwNjQ2YQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-white text-sm md:text-base hover:text-[#C9A961] transition-colors cursor-pointer">
                @joshuastinnett
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-10 md:py-16 px-4 md:px-6 relative" style={{ backgroundImage: 'url(https://static.readdy.ai/image/9da899e6632b3980025b2970c57bca5e/22427efad05f543d534570106432bf64.png)', backgroundSize: '300px', backgroundRepeat: 'repeat' }}>
        <div className="absolute inset-0 bg-black/90"></div>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-8 md:gap-12 mb-8 md:mb-12">
            <div className="md:col-span-4">
              <div className="flex items-center gap-3 mb-5 md:mb-6">
                <img src="https://public.readdy.ai/ai/img_res/431d0d82-5d35-4e5b-9098-9c3881ac0720.png" alt="Joshua Stinnett Hair Logo" className="h-10 w-auto object-contain" />
              </div>
              <p className="text-gray-400 leading-relaxed mb-4 text-sm">
                Celebrity hairstylist and color specialist based in West Hollywood. Nearly two decades of experience in precision cutting, advanced blonding, and luxury client service.
              </p>
              <div className="mb-5 md:mb-6 space-y-1">
                <a href="mailto:JoshuaStinnett85@gmail.com" className="flex items-center gap-2 text-gray-400 hover:text-[#C9A961] transition-colors text-sm cursor-pointer">
                  <i className="ri-mail-line text-[#C9A961]"></i> JoshuaStinnett85@gmail.com
                </a>
                <a href="tel:3107708491" className="flex items-center gap-2 text-gray-400 hover:text-[#C9A961] transition-colors text-sm cursor-pointer">
                  <i className="ri-phone-line text-[#C9A961]"></i> 310.770.8491
                </a>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://www.instagram.com/joshuastinnett?igsh=NTc4MTIwNjQ2YQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-[#C9A961] hover:text-[#C9A961] transition-all cursor-pointer"><i className="ri-instagram-line"></i></a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-[#C9A961] hover:text-[#C9A961] transition-all cursor-pointer"><i className="ri-facebook-fill"></i></a>
                <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-[#C9A961] hover:text-[#C9A961] transition-all cursor-pointer"><i className="ri-pinterest-line"></i></a>
                <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-[#C9A961] hover:text-[#C9A961] transition-all cursor-pointer"><i className="ri-tiktok-line"></i></a>
              </div>
            </div>
            <div className="md:col-span-3">
              <h4 className="text-white text-sm font-medium tracking-widest uppercase mb-5 md:mb-6">Quick Links</h4>
              <ul className="space-y-3 md:space-y-4">
                <li><Link to="/services" className="text-gray-400 hover:text-[#C9A961] transition-colors cursor-pointer text-sm">Services</Link></li>
                <li><a href="#gallery" className="text-gray-400 hover:text-[#C9A961] transition-colors cursor-pointer text-sm">Gallery</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-[#C9A961] transition-colors cursor-pointer text-sm">About Us</a></li>
                <li><a href="https://joshuastinnetthair.booksy.com/a/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C9A961] transition-colors cursor-pointer text-sm">Booking</a></li>
                <li><Link to="/media" className="text-gray-400 hover:text-[#C9A961] transition-colors cursor-pointer text-sm">Press</Link></li>
              </ul>
            </div>
            <div className="md:col-span-3">
              <h4 className="text-white text-sm font-medium tracking-widest uppercase mb-5 md:mb-6">Stay Updated</h4>
              <p className="text-gray-400 mb-4 md:mb-6 text-sm">Subscribe for styling tips and exclusive offers</p>
              <div className="flex gap-2">
                <input type="email" placeholder="Your email" className="flex-1 bg-[#2A2A2A] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A961] placeholder-gray-500 text-sm min-w-0" />
                <button className="bg-[#C9A961] text-white px-5 py-3 rounded-lg hover:bg-[#B89851] transition-all whitespace-nowrap cursor-pointer flex-shrink-0">
                  <i className="ri-send-plane-fill"></i>
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-4">We respect your privacy</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-5 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-gray-500 text-xs md:text-sm text-center">© 2025 Joshua Stinnett. All rights reserved.</p>
            <div className="flex items-center gap-3 md:gap-6 text-xs md:text-sm flex-wrap justify-center">
              <a href="#" className="text-gray-500 hover:text-[#C9A961] transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-gray-700">·</span>
              <a href="#" className="text-gray-500 hover:text-[#C9A961] transition-colors cursor-pointer">Terms</a>
              <span className="text-gray-700">·</span>
              <a href="#" className="text-gray-500 hover:text-[#C9A961] transition-colors cursor-pointer">Sitemap</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Lightbox Modal */}
      {lightboxSrc && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={closeLightbox}>
          <button onClick={closeLightbox} className="absolute top-4 right-4 md:top-6 md:right-8 text-white/70 hover:text-white transition-colors cursor-pointer w-10 h-10 flex items-center justify-center">
            <i className="ri-close-line text-3xl"></i>
          </button>
          <div className="max-w-5xl max-h-[90vh] w-full mx-4 md:mx-6 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxSrc} alt="Gallery full view" className="w-full h-full object-contain max-h-[90vh]" />
          </div>
        </div>
      )}
    </div>
  );
}
